import type { ReactElement } from "react";

import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { LeadPriorityScore } from "app/pages/sales-intelligence/domain/leads/types/lead-priority";
import {
    StyledCheckboxFilterContainer,
    StyledCheckboxFilterItemWrapper,
} from "app/pages/sales-intelligence/modules/advanced-search-filtering/components/filters/CheckboxFilter/styles";
import type { ICheckboxFilterOption } from "app/pages/sales-intelligence/modules/advanced-search-filtering/components/filters/CheckboxFilter/types";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import LeadScoreChip from "app/pages/sales-intelligence/modules/lead-scoring-agent/components/LeadScoreChip/LeadScoreChip";
import type LeadScoreFilterModel from "app/pages/sales-intelligence/modules/leads-search-filters/models/LeadScoreFilter/LeadScoreFilterModel";
import type { LeadScoreFilterOption } from "app/pages/sales-intelligence/modules/leads-search-filters/models/LeadScoreFilter/const";
import {
    isLeadScoreSelected,
    toggleLeadScoreSelection,
} from "app/pages/sales-intelligence/modules/leads-search-filters/models/LeadScoreFilter/leadScoreFilterValue";

import { LeadScoreDisabledTierTooltip } from "./LeadScoreDisabledTierTooltip";
import { LeadScoreAddRuleCombobox } from "./LeadScoreAddRuleCombobox";
import { LeadScoreFilterEmptyState } from "./LeadScoreFilterEmptyState";
import { StyledLeadScoreCheckbox, StyledLeadScoreFilterOptionRow } from "./styles";

type Props = IFilterViewBaseProps<LeadScoreFilterModel> & {
    onAddRule: (priority: LeadPriorityScore) => void;
};

export const LeadScoreFilterView = ({ filter, value, onFilterUpdate, onAddRule }: Props): ReactElement | null => {
    const handleTypeSelection = (selectedItem: LeadScoreFilterOption) => () => {
        onFilterUpdate(toggleLeadScoreSelection(selectedItem, value));
    };

    if (!filter.hasScoringRules) {
        return <LeadScoreFilterEmptyState onAddRule={onAddRule} />;
    }

    const renderOption = (option: ICheckboxFilterOption<LeadScoreFilterOption>) => {
        const isSelected = isLeadScoreSelected(option.value, value);
        const isNoScore = option.value === LeadPriorityScore.NoScore;
        const showScoreIcon = !isNoScore;
        const isTierEnabled = filter.enabledLeadScoreTiers.has(option.value);

        const row = (
            <StyledLeadScoreFilterOptionRow $isDisabled={!isTierEnabled}>
                <Flex
                    alignItems="center"
                    gap="8px"
                    data-automation={`lead-score-filter-option-${option.value}`}
                >
                    <StyledLeadScoreCheckbox
                        selected={isSelected}
                        label=""
                        onClick={isTierEnabled ? handleTypeSelection(option.value) : undefined}
                    />
                    {showScoreIcon ? (
                        <LeadScoreChip
                            score={option.value}
                            hideLabel
                        />
                    ) : null}
                    {isNoScore ? (
                        <LeadScoreChip score={option.value} />
                    ) : (
                        <Text fontVariant="body1">{option.label}</Text>
                    )}
                </Flex>
            </StyledLeadScoreFilterOptionRow>
        );

        return (
            <StyledCheckboxFilterItemWrapper key={option.value}>
                {!isTierEnabled && option.value !== LeadPriorityScore.NoScore ? (
                    <LeadScoreDisabledTierTooltip
                        priority={option.value}
                        onAddRule={onAddRule}
                    >
                        {row}
                    </LeadScoreDisabledTierTooltip>
                ) : (
                    row
                )}
            </StyledCheckboxFilterItemWrapper>
        );
    };

    return (
        <Stack
            gap="8px"
            width="100%"
            data-automation="lead-score-filter-options"
        >
            <StyledCheckboxFilterContainer>
                {filter.options.map((item) => renderOption(item))}
            </StyledCheckboxFilterContainer>
            <Flex
                justifyContent="center"
                width="100%"
            >
                <LeadScoreAddRuleCombobox onSelectPriority={onAddRule} />
            </Flex>
        </Stack>
    );
};
